/**
 * Shared helper for S3 presigned POST uploads
 *
 * Validates presigned data, uploads the file, and throws on failure.
 * This prevents silent upload failures that create "ghost images" in the UI.
 */

import {base64ToBlob} from '@/util';

export interface PresignedPostData {
    url: string;
    fields: Record<string, string | undefined>;
    key: string;
}

export interface UploadPresignedPostOptions {
    /** Content type override (default: from presigned fields or 'image/png') */
    contentType?: string;
    /** Timeout in ms (default: 30000) */
    timeout?: number;
}

/**
 * Upload a file using S3 presigned POST
 *
 * @throws {Error} If presigned data is invalid
 * @throws {Error} If upload fails (non-2xx response)
 * @returns The final URL of the uploaded file
 *
 * @example
 * ```ts
 * const presignedData = await getPresignedUrl();
 * const imageBlob = base64ToBlob(imageBase64, 'image/png');
 * const finalUrl = await uploadPresignedPost(presignedData, imageBlob);
 * ```
 */
export async function uploadPresignedPost(
    presignedData: PresignedPostData,
    file: Blob,
    options: UploadPresignedPostOptions = {}
): Promise<string> {
    const {timeout = 30000} = options;

    // Validate presigned data
    if (!presignedData.url) {
        throw new Error('Presigned upload failed: missing URL');
    }
    if (!presignedData.fields) {
        throw new Error('Presigned upload failed: missing fields');
    }
    if (!presignedData.key) {
        throw new Error('Presigned upload failed: missing key');
    }

    // Build form data
    const formData = new FormData();

    // Add all fields from presigned response (order matters for S3)
    for (const [key, value] of Object.entries(presignedData.fields)) {
        if (value !== undefined) {
            formData.append(key, String(value));
        }
    }

    // Add key and file last
    formData.append('key', presignedData.key);
    formData.append('file', file, presignedData.key);

    // Upload with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(presignedData.url, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
        });

        if (!response.ok) {
            // Try to get error details from response
            let errorDetail = '';
            try {
                const text = await response.text();
                if (text) errorDetail = `: ${text.slice(0, 200)}`;
            } catch {
                // Ignore parse errors
            }
            throw new Error(
                `Presigned upload failed: ${response.status} ${response.statusText}${errorDetail}`
            );
        }

        // Return the final URL
        return `${presignedData.url}/${presignedData.key}`;
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Presigned upload timed out after ${timeout}ms`);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * Upload a base64-encoded image using S3 presigned POST
 *
 * Convenience wrapper that handles base64 → Blob conversion.
 *
 * @throws {Error} If presigned data is invalid
 * @throws {Error} If upload fails (non-2xx response)
 * @returns The final URL of the uploaded image
 */
export async function uploadPresignedPostBase64(
    presignedData: PresignedPostData,
    imageBase64: string,
    options: UploadPresignedPostOptions = {}
): Promise<string> {
    const contentType =
        options.contentType ??
        presignedData.fields['Content-Type'] ??
        'image/png';
    const imageBlob = base64ToBlob(imageBase64, contentType);

    return uploadPresignedPost(presignedData, imageBlob, options);
}
